import React from 'react';
import * as Redux from 'react-redux';
import { Navigate, Routes, Route, useSearchParams } from 'react-router-dom';

import GroupContainer from './group-container';
import Layout from './layout';
import load from '../components/asyncComponent';
import updateCreator from '../actions/update-creator';
import fetchSettings from '../actions/fetch-settings';
import fetchPageText from '../actions/fetch-page-text';

import {
  APPOINTMENT_STEP,
  COLOR_STEP,
  CONTACT_STEP,
  CONFIGURABLE_FIELDS_STEP,
  DEVICE_BRAND_STEP,
  DEVICE_MODEL_STEP,
  DEVICE_TYPE_STEP,
  ISSUE_STEP,
  LOCATION_STEP,
  ON_SITE_STEP,
  PAYMENT_STEP,
  SERVICE_OPTION_STEP,
  STORE_LIST_STEP,
  SUBMIT_STEP,
} from '../steps';

const Landing = load(() => import('./landing'));
const Tracking = load(() => import('./tracking/'));
const BatchTracking = load(() => import('./tracking/batch'));
const AccountInactive = load(() => import('./landing/inactive'));
const DeviceBrandSelection = load(() => import('./device/brand-selection'));
const DeviceColorSelection = load(() => import('./device/color-selection'));
const DeviceModelSelection = load(() => import('./device/model-selection'));
const ConfigurableFields = load(() => import('./device/configurable-fields'));
const OnSiteLocation = load(() => import('./on-site-location'));
const Issue = load(() => import('./issue'));
const Time = load(() => import('./time/index'));
const Location = load(() => import('./location/index'));
const Contact = load(() => import('./contact'));
const Payment = load(() => import('./payment'));
const ServiceOption = load(() => import('./service-option'));
const StoreList = load(() => import('./store-list'));
const Submit = load(() => import('./submit'));
const DeviceTypeSelection = load(() => import('./device/type-selection'));

const requestRoutes = [
  {
    component: DeviceTypeSelection,
    header: 'What type of device do you have?',
    key: 'device',
    label: 'Device',
    path: 'device',
    step: DEVICE_TYPE_STEP,
  },
  {
    component: ConfigurableFields,
    header: 'We need a few more details about your device',
    key: 'configurable-fields',
    label: 'Device',
    path: 'configurable-fields',
    step: CONFIGURABLE_FIELDS_STEP,
  },
  {
    component: DeviceBrandSelection,
    key: 'brand',
    label: 'Device',
    path: 'brand',
    step: DEVICE_BRAND_STEP,
  },
  {
    component: DeviceModelSelection,
    key: 'model',
    label: 'Device',
    path: 'model',
    step: DEVICE_MODEL_STEP,
  },
  {
    component: DeviceColorSelection,
    key: 'color',
    label: 'Device',
    path: 'color',
    step: COLOR_STEP,
  },
  {
    component: Issue,
    header: 'What is the issue?',
    key: 'issue',
    label: 'Issue',
    path: 'issue',
    step: ISSUE_STEP,
  },
  {
    component: Location,
    key: 'location',
    label: 'Location',
    path: 'location',
    step: LOCATION_STEP,
  },
  {
    component: ServiceOption,
    key: 'service-option',
    label: 'Location',
    path: 'service-option',
    step: SERVICE_OPTION_STEP,
  },
  {
    component: OnSiteLocation,
    key: 'on-site-location',
    label: 'Location',
    path: 'on-site-location',
    step: ON_SITE_STEP,
  },
  {
    component: StoreList,
    key: 'in-store-location',
    label: 'Location',
    path: 'in-store-location',
    step: STORE_LIST_STEP,
  },
  {
    component: Time,
    key: 'time',
    label: 'Time',
    path: 'time',
    step: APPOINTMENT_STEP,
  },
  {
    component: Contact,
    header: 'How should we contact you?',
    key: 'contact',
    label: 'Contact',
    path: 'contact',
    step: CONTACT_STEP,
  },
  {
    component: Payment,
    key: 'payment',
    label: 'Payment',
    path: 'payment',
    step: PAYMENT_STEP,
  },
  {
    component: Submit,
    key: 'submit',
    label: 'Submit',
    path: 'submit',
    step: SUBMIT_STEP,
  },
];

/** Returns array of request routes containing a new 'labelTranslated' field */
const getTranslatedRoutes = progress => {
  const routesTranslated = requestRoutes.map(route => {
    const labelTranslated = progress[route.label.toLowerCase()];
    route.labelTranslated = labelTranslated ? labelTranslated : route.label;
    return route;
  });

  return routesTranslated;
};

function AppRoutes() {
  const dispatch = Redux.useDispatch();
  const [searchParams] = useSearchParams();
  const creatorId = searchParams.get('creatorId');
  const { progress } = Redux.useSelector(state => state.pageText);

  React.useEffect(() => {
    dispatch(fetchPageText());
    dispatch(fetchSettings());

    if (creatorId) {
      dispatch(updateCreator({ creatorId }));
    }
  }, [dispatch, creatorId]);

  const renderRoutes = () => {
    const routesTranslated = getTranslatedRoutes(progress);
    return routesTranslated.map(requestRoute => (
      <Route
        element={
          <GroupContainer>
            <Layout route={requestRoute} routes={routesTranslated}>
              {React.createElement(requestRoute.component, {
                step: requestRoute.step,
                label: requestRoute.label,
                header: requestRoute.header,
              })}
            </Layout>
          </GroupContainer>
        }
        key={requestRoute.path}
        path={`/requests/:groupSlug/${requestRoute.path}`}
      />
    ));
  };

  return (
    <Routes>
      <Route element={<Landing />} path="/requests/:groupSlug" />
      {progress ? renderRoutes() : null}
      <Route element={<Tracking />} path="/tracking/:repairId" />
      <Route element={<Tracking />} path="/tracking/:repairId/:defaultRating" />
      <Route element={<BatchTracking />} path="/batch/:batchId" />
      <Route element={<AccountInactive />} path="/inactive/:groupSlug" />
      <Route
        element={<Navigate replace to="/requests/*" />}
        path="/repairs/*"
      />
      <Route element={<Navigate replace to="/requests/consumer" />} path="/" />
    </Routes>
  );
}

export default AppRoutes;
